import { parseNumberBR } from "./utils";
import { isDataISOValida } from "./validators/date";

/**
 * Capitaliza a primeira letra de uma string.
 */
export function capitalizeFirstLetter(value: string): string {
	if (!value) return value;
	return value.replace(/^./, (c) => c.toUpperCase());
}

/**
 * Remove tudo que não for dígito.
 */
export function unmaskPhoneBR(value: string): string {
	return (value ?? "").replaceAll(/\D/g, "");
}

/**
 * Mascara telefone BR (progressiva).
 */
export function maskPhoneBR(value: string): string {
	const digits = unmaskPhoneBR(value).slice(0, 11);
	if (!digits) return "";

	const ddd = digits.slice(0, 2);
	const rest = digits.slice(2);

	if (digits.length <= 2) return `(${ddd}`;

	if (digits.length >= 11) return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5, 9)}`;

	if (digits.length === 10) return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4, 8)}`;

	const isLikelyMobile = rest.startsWith("9");
	const splitIndex = isLikelyMobile ? 5 : 4;
	const first = rest.slice(0, splitIndex);
	const second = rest.slice(splitIndex);

	return second ? `(${ddd}) ${first}-${second}` : `(${ddd}) ${first}`;
}

/**
 * Formata número no padrão pt-BR.
 */
export function formatNumberBR(value: unknown, opts: Intl.NumberFormatOptions = {}, ShowSignal?: boolean): string {
	const n = parseNumberBR(value);
	if (n === null) return "-";

	const hasMax = typeof opts.maximumFractionDigits === "number";
	const hasMin = typeof opts.minimumFractionDigits === "number";
	const min = hasMin ? opts.minimumFractionDigits : undefined;
	const hasShowSignal = ShowSignal === true;

	const isInt = Number.isInteger(n);

	const defaults: Intl.NumberFormatOptions = {};

	if (!hasMax) {
		const baseMax = isInt ? 0 : 2;
		defaults.maximumFractionDigits = typeof min === "number" ? Math.max(baseMax, min) : baseMax;
	}

	if (!hasMin && opts.style === "currency") defaults.minimumFractionDigits = 2;

	return new Intl.NumberFormat("pt-BR", {
		...defaults,
		...opts,
		signDisplay: hasShowSignal ? "always" : undefined,
	}).format(n);
}

/**
 * Formata moeda BRL no padrão pt-BR.
 */
export function formatCurrencyBRL(value: unknown): string {
	const n = parseNumberBR(value);
	if (n === null) return "-";

	return new Intl.NumberFormat("pt-BR", {
		style: "currency",
		currency: "BRL",
	}).format(n);
}

/**
 * Remove tudo que não é dígito.
 * Usado como unmask em formulários de moeda.
 */
export function unmaskCurrencyBRL(raw: string): string {
	return (raw ?? "").replaceAll(/\D/g, "");
}

/**
 * Formata um valor monetário de forma compacta (R$ 1,2M / R$ 8,9k),
 * recorrendo a {@link formatCurrencyBRL} para valores abaixo de mil.
 */
export function formatCompactCurrencyBRL(valor: number): string {
	if (!Number.isFinite(valor)) return "-";

	const absoluto = Math.abs(valor);
	if (absoluto >= 1e6) return `R$ ${(valor / 1e6).toFixed(1).replace(".", ",")}M`;
	if (absoluto >= 1e3) return `R$ ${(valor / 1e3).toFixed(1).replace(".", ",")}k`;

	return formatCurrencyBRL(valor);
}

/**
 * Converte string de centavos para display BRL sem símbolo: "1000" → "10,00".
 * Usado como mask em inputs de moeda.
 */
export function maskCurrencyBRL(cents: string): string {
	if (!cents) return "";
	const num = parseInt(cents, 10);
	if (Number.isNaN(num)) return "";
	return (num / 100).toLocaleString("pt-BR", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

/**
 * Converte string de centavos para número decimal:
 * "1000" → 10.00
 * ""     → 0
 */
export function unmaskCurrencyBRLToDecimal(cents: string): number {
	if (!cents) return 0;
	const num = parseInt(cents, 10);
	return Number.isNaN(num) ? 0 : num / 100;
}

/** Remove a máscara decimal e mantém o valor interno em centésimos. */
export const unmaskDecimalBR = (value: string): string => (value ?? "").replaceAll(/\D/g, "");

/** Formata centésimos como decimal brasileiro com duas casas, sem símbolo monetário. */
export const maskDecimalBR = (value: string): string => {
	if (!value) return "";

	const numberValue = Number.parseInt(value, 10);
	if (Number.isNaN(numberValue)) return "";

	return (numberValue / 100).toLocaleString("pt-BR", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

/**
 * Máscara simples de competência MM/YYYY.
 * Entrada progressiva: "03" → "03", "032026" → "03/2026"
 */
export function maskCompetencia(value: string): string {
	const digits = (value ?? "").replaceAll(/\D/g, "").slice(0, 6);
	if (!digits) return "";
	if (digits.length <= 2) return digits;
	return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

/** Remove máscara de competência: "03/2026" → "032026" */
export function unmaskCompetencia(value: string): string {
	return (value ?? "").replaceAll(/\D/g, "");
}

/**
 * Máscara progressiva de data BR digitando: DD/MM/AAAA
 */
export function maskDateBR(value: string): string {
	const digits = (value ?? "").replaceAll(/\D/g, "").slice(0, 8);
	if (!digits) return "";
	if (digits.length <= 2) return digits;
	if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
	return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * ISO date-only -> Date (sem shift de timezone)
 */
export function isoDateOnlyToDate(value: unknown): Date | null {
	if (!isDataISOValida(value)) return null;
	const [yyyy, mm, dd] = value.split("-").map(Number);
	return new Date(yyyy, mm - 1, dd);
}

/**
 * Date -> ISO date-only (YYYY-MM-DD)
 */
export function dateToISODateOnly(date: Date): string {
	const yyyy = String(date.getFullYear()).padStart(4, "0");
	const mm = String(date.getMonth() + 1).padStart(2, "0");
	const dd = String(date.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

export type InputTokenMode = "text" | "number" | "integer";

/**
 * Sanitiza e valida entrada de token baseado no modo especificado.
 *
 * @param token - A string de token a sanitizar. Tratada como string vazia se nula ou indefinida.
 * @param mode - O modo de entrada que determina as regras de sanitização:
 *   - 'text': Remove todos os dígitos e mantém letras/símbolos (minúsculo).
 *   - 'number': Permite dígitos, um sinal de menos opcional no início, e um único ponto decimal.
 *   - 'integer': Permite dígitos e um sinal de menos opcional no início (sem ponto decimal).
 *
 * @returns A string sanitizada, ou `''` se a entrada estiver vazia após trim.
 *
 * @example
 * ```ts
 * sanitizeTokenInput('Abc123', 'text');       // 'abc'
 * sanitizeTokenInput('-1.23x', 'number');     // '-1.23'
 * sanitizeTokenInput('R$ -1.234,56', 'number'); // '-1.23456' (remove vírgula e símbolos)
 * sanitizeTokenInput('R$ -1.234,56', 'integer'); // '-123456'
 * ```
 */
export function maskCPF(value: string): string {
	const digits = (value ?? "").replaceAll(/\D/g, "").slice(0, 11);
	if (!digits) return "";
	return digits
		.replace(/(\d{3})(\d)/, "$1.$2")
		.replace(/(\d{3})(\d)/, "$1.$2")
		.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

export function maskCNPJ(value: string): string {
	const digits = (value ?? "").replaceAll(/\D/g, "").slice(0, 14);
	if (!digits) return "";
	return digits
		.replace(/(\d{2})(\d)/, "$1.$2")
		.replace(/(\d{3})(\d)/, "$1.$2")
		.replace(/(\d{3})(\d)/, "$1/$2")
		.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}

export const CHAVE_NFE_DIGITOS = 44;
export const CHAVE_NFE_MASCARA_LENGTH = 54;

export function unmaskChaveNFe(value: string): string {
	return (value ?? "").replaceAll(/\D/g, "").slice(0, CHAVE_NFE_DIGITOS);
}

/**
 * Máscara da chave da NFe em blocos de 4: "4225 0707 5990 9000 ...".
 */
export function maskChaveNFe(value: string): string {
	const digits = unmaskChaveNFe(value);
	if (!digits) return "";
	return digits.replaceAll(/(\d{4})(?=\d)/g, "$1 ");
}

export function unmaskCPF(value: string): string {
	return (value ?? "").replaceAll(/\D/g, "").slice(0, 11);
}

export function unmaskCNPJ(value: string): string {
	return (value ?? "").replaceAll(/\D/g, "").slice(0, 14);
}

export function unmaskCpfOrCnpj(value: string): string {
	return (value ?? "").replaceAll(/\D/g, "");
}

export function sanitizeTokenInput(token: string, mode: InputTokenMode): string {
	const trimmed = (token ?? "").trim().toLowerCase();
	if (!trimmed) return "";

	if (mode === "text") return trimmed.replaceAll(/\d+/g, "").trim().toLowerCase();

	if (mode === "integer") {
		// integer mode: permite dígitos e um sinal de menos opcional no início
		let out = "";
		let hasMinus = false;

		for (const ch of trimmed)
			if (ch >= "0" && ch <= "9") out += ch;
			else if (ch === "-" && !hasMinus && out.length === 0) {
				hasMinus = true;
				out += ch;
			}

		return out;
	}

	// number mode: permite dígitos, um sinal de menos opcional no início, e um único ponto decimal
	let out = "";
	let hasDot = false;
	let hasMinus = false;

	for (const ch of trimmed)
		if (ch >= "0" && ch <= "9") out += ch;
		else if (ch === "." && !hasDot) {
			hasDot = true;
			out += ch;
		} else if (ch === "-" && !hasMinus && out.length === 0) {
			hasMinus = true;
			out += ch;
		}

	return out;
}

/**
 * Retorna as iniciais de um nome.
 *
 * A função considera a primeira letra da primeira e da segunda palavra do nome.
 * Caso o nome esteja vazio ou não possua caracteres válidos, retorna `"?"`.
 *
 * @param nome - Nome que será usado para gerar as iniciais.
 * @returns As iniciais do nome em letras maiúsculas.
 *
 * @example
 * obterIniciaisNome("João Silva");
 * // Retorna: "JS"
 *
 * @example
 * obterIniciaisNome("Maria");
 * // Retorna: "M"
 *
 * @example
 * obterIniciaisNome("");
 * // Retorna: "?"
 */
export function obterIniciaisNome(nome: string): string {
	const partes = nome.trim().split(/\s+/);
	const primeira = partes[0]?.[0] ?? "";
	const segunda = partes[1]?.[0] ?? "";

	return (primeira + segunda).toUpperCase() || "?";
}
